import React, { useMemo, useRef, useState, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import styles from './widget-progress.module.scss'
import progressIconImage from './assets/widget-progress-icon.svg'
import progressIconImageGray from './assets/widget-progress-icon-gray.svg'

export const WidgetProgress = ({
  stats: { completed, total } = {},
  size,
  variant,
  dateInfo,
  showProgressIcon = false,
  progressText = '',
  status,
  statsIcon = null,
  hideLabel = false,
  alwaysFillProgress = false,
}) => {
  const computed = useMemo(() => {
    const isMobile = variant === 'mobile'
    const isDesktop = variant === 'desktop'
    const isLarge = size === 'large'
    const isSmall = size === 'small'
    const isFailedStatus = status === 'failed' || status === 'none'
    const isFailed = isFailedStatus && !alwaysFillProgress

    const rawPercentage = (completed / total) * 100
    const percentage = Math.min(rawPercentage, 100)

    return {
      percentage,
      hasProgress: completed > 0,
      isMobile,
      isDesktop,
      isLarge,
      isSmall,
      isMobileSmall: isMobile && isSmall,
      isDesktopLarge: isDesktop && isLarge,
      isDesktopSmall: isDesktop && isSmall,
      isFailed,
      showProgressBar: !(isMobile && isSmall),
      iconSrc: isFailedStatus ? progressIconImageGray : progressIconImage,
    }
  }, [completed, total, variant, size, status, alwaysFillProgress])

  const {
    percentage,
    hasProgress,
    isDesktop,
    isDesktopLarge,
    isMobileSmall,
    isFailed,
    showProgressBar,
    iconSrc,
  } = computed

  const progressClass = classNames(styles.progress, {
    [styles.desktop]: isDesktop,
    [styles.mobile]: computed.isMobile,
    [styles.desktopLarge]: isDesktopLarge,
    [styles.desktopSmall]: computed.isDesktopSmall,
  })

  const statsClass = classNames(styles.stats, {
    [styles.statsMobileSmall]: isMobileSmall || isDesktop,
  })

  const progressBarClass = classNames(styles.progressBar, {
    [styles.progressBarFailed]: isFailed,
  })

  const numberClass = classNames(styles.number, {
    [styles.numberLarge]: isDesktopLarge,
  })

  const failedNumberClass = classNames(numberClass, styles.numberFailed)
  const failedTotalClass = classNames(styles.total, styles.totalFailed)

  return (
    <div className={progressClass}>
      {showProgressBar && (
        <div className={styles.progressBarWrapper}>
          <div className={progressBarClass}>
            {hasProgress && !isFailed && (
              <div className={styles.progressFill} style={{ width: `${percentage}%` }} />
            )}

            {isFailed ? (
              <div className={styles.failedContent}>
                <div className={statsClass}>
                  <span className={failedNumberClass}>{completed}</span>
                  <span className={failedTotalClass}>/{total}</span>
                </div>
              </div>
            ) : (
              progressText && <div className={styles.progressText}>{progressText}</div>
            )}

            {showProgressIcon && (
              <div className={styles.progressIcon}>
                <img src={iconSrc} alt="" className={styles.iconImage} />
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.labelRow}>
        <div className={statsClass}>
          {statsIcon && (
            <div className={styles.statsIcon}>
              <img src={statsIcon} alt="" className={styles.statsIconImage} />
            </div>
          )}
          <span className={numberClass}>{completed}</span>
          <span className={styles.total}>/{total}</span>
          {!hideLabel && <span className={styles.label}>Выполненных заданий</span>}
        </div>

        {dateInfo && <p className={styles.dateInfo}>{dateInfo}</p>}
      </div>
    </div>
  )
}

WidgetProgress.propTypes = {
  stats: PropTypes.shape({
    completed: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
  size: PropTypes.oneOf(['small', 'large']),
  variant: PropTypes.oneOf(['mobile', 'desktop']),
  dateInfo: PropTypes.string,
  showProgressIcon: PropTypes.bool,
  progressText: PropTypes.string,
  status: PropTypes.oneOf(['completed', 'pending', 'failed', 'none']),
  statsIcon: PropTypes.string,
  hideLabel: PropTypes.bool,
  alwaysFillProgress: PropTypes.bool,
}

WidgetProgress.defaultProps = {
  size: 'small',
  variant: 'desktop',
  showProgressIcon: false,
  progressText: '',
  hideLabel: false,
  statsIcon: null,
  alwaysFillProgress: false,
}

export const ProgressBar = ({
  stats: { completed, total } = {},
  variant,
  progressText,
  status,
  hideFill = false,
}) => {
  const textRef = useRef()
  const progressBarRef = useRef()
  const [isTextInsideFill, setIsTextInsideFill] = useState(false)
  const isFailed = status === 'failed' || status === 'none'
  const isDesktop = variant === 'desktop'
  const rawPercentage = (completed / total) * 100
  const percentage = Math.min(rawPercentage, 100)
  const hasProgress = completed > 0

  useLayoutEffect(() => {
    const textEl = textRef.current
    const fillEl = progressBarRef.current
    if (!textEl || !fillEl) {
      setIsTextInsideFill(false)
      return
    }
    const text = textEl.getBoundingClientRect()
    const fill = fillEl.getBoundingClientRect()
    setIsTextInsideFill(fill.right - text.right > 0)
  }, [completed, total, progressText, variant, hideFill])

  const wrapperClass = classNames(styles.progressBarWrapper, styles.progressBarWrapperStandalone)
  const barClass = classNames(styles.progressBar, styles.progressBarStandalone, {
    [styles.progressBarFailed]: isFailed,
    [styles.desktop]: isDesktop,
    [styles.noFill]: hideFill,
  })

  return (
    <div className={wrapperClass}>
      <div className={barClass}>
        {!hideFill && hasProgress && !isFailed && (
          <div
            className={styles.progressFill}
            style={{ width: `${percentage}%` }}
            ref={progressBarRef}
          />
        )}
        {progressText && (
          <div
            className={classNames(styles.progressText, {
              [styles.textNotFilled]: !isTextInsideFill,
            })}
            ref={textRef}
          >
            {progressText}
          </div>
        )}
      </div>
    </div>
  )
}

ProgressBar.propTypes = {
  stats: PropTypes.shape({
    completed: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
  variant: PropTypes.oneOf(['mobile', 'desktop']),
  progressText: PropTypes.string,
  status: PropTypes.oneOf(['completed', 'pending', 'failed', 'none']),
  hideFill: PropTypes.bool,
}

ProgressBar.defaultProps = {
  variant: 'desktop',
  progressText: '',
  hideFill: false,
}

export const StatsCounter = ({ stats: { completed, total }, variant, dateInfo, statsIcon }) => {
  const isDesktop = variant === 'desktop'
  const isMobile = variant === 'mobile'

  const labelRowClass = classNames(styles.labelRow, {
    [styles.mobile]: isMobile,
    [styles.desktop]: isDesktop,
  })

  const statsClass = classNames(styles.stats, {
    [styles.statsMobileSmall]: isDesktop,
  })

  return (
    <div className={labelRowClass}>
      <div className={statsClass}>
        {statsIcon && (
          <div className={styles.statsIcon}>
            <img src={statsIcon} alt="" className={styles.statsIconImage} />
          </div>
        )}
        <span className={styles.number}>{completed}</span>
        <span className={styles.total}>/{total}</span>
      </div>

      {dateInfo && <p className={styles.dateInfo}>{dateInfo}</p>}
    </div>
  )
}

StatsCounter.propTypes = {
  stats: PropTypes.shape({
    completed: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
  variant: PropTypes.oneOf(['mobile', 'desktop']),
  dateInfo: PropTypes.string,
  statsIcon: PropTypes.string,
}

StatsCounter.defaultProps = {
  variant: 'desktop',
}
